<template>
  <div class="wrapper">
    <FORM ref="childForm" @formclick="formclick" :formData="formData"></FORM>
    <div class="top-scrollbar" ref="topScroll" @scroll="handleTopScroll">
      <div class="top-scrollbar-content" :style="{ width: tableContentWidth + 'px' }"></div>
    </div>
    <table-page ref="childTable" :pageData="pageData" @getTableData="handleTableData"></table-page>
  </div>
</template>

<script>
import tablePage from '@/components/page/table.vue'
import FORM from '@/components/page/form.vue'
import URL from '@/views/analyse/util/url'

// UTC+8 時間格式化工具
const formatUTC8 = (data) => {
  if (!data) return '';
  const date = new Date(data);
  if (isNaN(date.getTime())) return data;
  const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
  const utc8Time = new Date(utc + 28800000);
  const pad = (n) => (n < 10 ? '0' + n : n);
  return `${utc8Time.getFullYear()}-${pad(utc8Time.getMonth() + 1)}-${pad(utc8Time.getDate())} ${pad(utc8Time.getHours())}:${pad(utc8Time.getMinutes())}:${pad(utc8Time.getSeconds())}`;
};

export default {
  name: 'RiskAnalyseByCustomer',
  components: {
    tablePage,
    FORM
  },
  data() {
    return {
      tableContentWidth: 0,
      activeScrollSource: null, 
      scrollTimer: null,
      formData: {
        hasLable: true,
        cols: [
          {
            title: window.$t('analyse.riskAnalyse.customerAccount'),
            field: 'customerNumbers',
            type: 1, 
            placeholder: window.$t('analyse.riskAnalyse.customerAccountPlaceholder')
          },
          {
            title: window.$t('analyse.riskAnalyse.queryDays'), 
            field: 'queryDays', 
            type: 2, 
            optionList: [
              { key: 30, value: window.$t('analyse.riskAnalyse.thirtyDays') }, 
              { key: 60, value: window.$t('analyse.riskAnalyse.sixtyDays') },
              { key: 365, value: window.$t('analyse.riskAnalyse.threeHundredSixtyFiveDays') }
            ], 
            data: 365,
            isNumber: true
          },
          {
            title: window.$t('analyse.riskAnalyse.customTime'),
            field: 'customTime',
            type: 6, // 日期範圍選擇器
            startEndParam: ['startTime', 'endTime'],
            format: 'yyyy-MM-dd HH:mm:ss'
          },
          {
            title: window.$t('analyse.riskAnalyse.shortTermSeconds'),
            field: 'shortTermSeconds',
            type: 7, // 數字輸入框
            data: 60,
            min: 1
          },
          { click: window.$t('analyse.bt.query') }
        ]
      },
      pageData: {
        isShow: false, 
        isPage: false, 
        queryUrl: URL.riskAnalyse.queryByCustomerNumber,
        method: 'get',
        selection: false,
        sequence: false,
        height: 'calc(100vh - 250px)',
        cols: [
          { title: window.$t('analyse.riskAnalyse.customerNumber'), field: 'customerNumber', fixed: 'left' },
          { title: window.$t('analyse.riskAnalyse.activateTime'), field: 'activateTime', render: (data) => formatUTC8(data), fixed: 'left', sortable: true },
          { title: window.$t('analyse.riskAnalyse.referrerNumber'), field: 'referrerNumber', render: (data) => !data ? window.$t('analyse.riskAnalyse.empty') : data, fixed: 'left'  },
          { title: window.$t('analyse.riskAnalyse.source'), field: 'source' },
          { title: window.$t('analyse.riskAnalyse.medium'), field: 'medium' },
          { title: window.$t('analyse.riskAnalyse.latestBalance'), field: 'latestBalance', fixed: 'left', sortable: true },
          { title: window.$t('analyse.riskAnalyse.latestEquity'), field: 'latestEquity', fixed: 'left', sortable: true },
          { title: window.$t('analyse.riskAnalyse.latestSettleDate'), field: 'latestSettleDate', fixed: 'left', sortable: true },
          { title: window.$t('analyse.riskAnalyse.firstTimeCashInCurrency'), field: 'firstTimeCashInCurrency' },
          { title: window.$t('analyse.riskAnalyse.totalCashIn'), field: 'totalCashIn', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalCashOut'), field: 'totalCashOut', sortable: true  },
          { title: window.$t('analyse.riskAnalyse.netCashIn'), field: 'netCashIn', sortable: true  },
          { title: window.$t('analyse.riskAnalyse.bonusSent'), field: 'bonusSent', sortable: true },
          { title: window.$t('analyse.riskAnalyse.bonusDeducted'), field: 'bonusDeducted', sortable: true },
          { title: window.$t('analyse.riskAnalyse.rewardSent'), field: 'rewardSent', sortable: true },
          { title: window.$t('analyse.riskAnalyse.rewardDeducted'), field: 'rewardDeducted', sortable: true },
          { title: window.$t('analyse.riskAnalyse.earnedBonus'), field: 'earnedBonus', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalTrade'), field: 'totalTrade', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalVolume'), field: 'totalVolume', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalProfit'), field: 'totalProfit', sortable: true },
          { title: window.$t('analyse.riskAnalyse.highProfitMultiple'), field: 'highProfitMultiple', sortable: true, render: (data) => data == null ? '' : Number(data).toFixed(2) },
          { title: window.$t('analyse.riskAnalyse.totalClearZero'), field: 'totalClearZero', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalCommission'), field: 'totalCommission', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalSwap'), field: 'totalSwap', sortable: true },
          { title: window.$t('analyse.riskAnalyse.shortTermTradeRatioPct'), field: 'shortTermTradeRatioPct', sortable: true },
          { title: window.$t('analyse.riskAnalyse.shortTermProfitRatioPct'), field: 'shortTermProfitRatioPct', sortable: true },
          { title: window.$t('analyse.riskAnalyse.top3ProfitableSymbols'), field: 'top3ProfitableSymbols', overflow: true },
          { title: window.$t('analyse.riskAnalyse.openIp'), field: 'openIp' },
          { title: window.$t('analyse.riskAnalyse.ipUsageCount'), field: 'ipUsageCount', sortable: true },
          { title: window.$t('analyse.riskAnalyse.sharedCustomerNumbers'), field: 'sharedCustomerNumbers', overflow: true },
          { title: window.$t('analyse.riskAnalyse.ipRegLocationCn'), field: 'ipRegLocationCn' },
          { title: window.$t('analyse.riskAnalyse.idRegLocationCn'), field: 'idRegLocationCn' },
          { title: window.$t('analyse.riskAnalyse.mobilePhoneRegLocationCn'), field: 'mobilePhoneRegLocationCn' }
        ]
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.bindTableScroll();
      window.addEventListener('resize', this.syncWidths);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncWidths);
  },
  methods: {
    formclick(clickName, data) {
      if (clickName === window.$t('analyse.bt.query') || clickName === '查詢') {
        const rawInput = data.customerNumbers;
        if (!rawInput) {
          this.$message.warning(window.$t('analyse.riskAnalyse.customerAccountEmpty'));
          return;
        }

        const normalizedStr = rawInput.replace(/，/g, ',').replace(/[\s\n]+/g, ',');
        const strArray = normalizedStr.split(',').filter(item => item.trim() !== '');

        if (strArray.length === 0) {
          this.$message.warning(window.$t('analyse.riskAnalyse.customerAccountEmpty'));
          return;
        }

        if (strArray.length > 200) {
          this.$message.error(window.$t('analyse.riskAnalyse.customerAccountLimit'));
          return;
        }

        const validNumbers = [];
        for (let i = 0; i < strArray.length; i++) {
          const num = Number(strArray[i]);
          if (isNaN(num) || !Number.isInteger(num)) {
            this.$message.error(window.$t('analyse.riskAnalyse.customerAccountError'));
            return;
          }
          validNumbers.push(num);
        }

        const finalNumbersStr = validNumbers.join(',');
        
        // 攔截過長的 GET URL 防禦機制 (1500 是保守估計安全範圍)
        if (finalNumbersStr.length > 1500) {
          this.$message.error(window.$t('analyse.riskAnalyse.uriTooLong'));
          return;
        }

        // 處理時間優先級邏輯：自定義時間 > 固定天數
        let finalStartTime = data.startTime;
        let finalEndTime = data.endTime;
        
        if (!finalStartTime || !finalEndTime) {
          const days = data.queryDays || 365;
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - days);
          finalStartTime = formatUTC8(start);
          finalEndTime = formatUTC8(end);
        }

        const payload = {
          ...data,
          customerNumbers: finalNumbersStr,
          startTime: finalStartTime,
          endTime: finalEndTime
        };
        
        // 刪除多餘欄位，避免干擾後端
        delete payload.queryDays;
        delete payload.customTime;

        this.$nextTick(() => {
          if (this.$refs.childTable) {
            this.$refs.childTable.defaultQueryData(payload);
          }
        });
      }
    },
    // 同步寬度與雙向滾動的代碼保持與 list.vue 完全一致
    syncWidths() {
      if (!this.$refs.childTable || !this.$refs.topScroll) return;
      const tableBodyWrapper = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBodyWrapper) {
        const tableMaxScroll = tableBodyWrapper.scrollWidth - tableBodyWrapper.clientWidth;
        const topClientWidth = this.$refs.topScroll.clientWidth;
        this.tableContentWidth = topClientWidth + tableMaxScroll;
      }
    },
    handleTableData(tableData) {
      if (!tableData || tableData.length === 0) {
        this.$message.info(window.$t('analyse.riskAnalyse.noRecord'));
      }
      setTimeout(() => { this.syncWidths(); }, 100);
    },
    handleTopScroll(e) {
      if (this.activeScrollSource === 'bottom') return;
      this.activeScrollSource = 'top';
      const tableBody = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBody && Math.abs(tableBody.scrollLeft - e.target.scrollLeft) > 1) {
        tableBody.scrollLeft = e.target.scrollLeft;
      }
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => { this.activeScrollSource = null; }, 50);
    },
    bindTableScroll() {
      const tableBody = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBody) {
        tableBody.addEventListener('scroll', (e) => {
          if (this.activeScrollSource === 'top') return;
          this.activeScrollSource = 'bottom';
          if (this.$refs.topScroll && Math.abs(this.$refs.topScroll.scrollLeft - e.target.scrollLeft) > 1) {
            this.$refs.topScroll.scrollLeft = e.target.scrollLeft;
          }
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => { this.activeScrollSource = null; }, 50);
        });
      }
    }
  }
}
</script>

<style scoped>
.wrapper { padding: 20px; }
.top-scrollbar { width: 96%; margin: 0 auto; overflow-x: auto; overflow-y: hidden; }
.top-scrollbar::-webkit-scrollbar { height: 12px; }
.top-scrollbar::-webkit-scrollbar-thumb { background: #c0c4cc; border-radius: 6px; }
.top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 6px; }
.top-scrollbar-content { height: 1px; }
</style>